import React, { useEffect } from 'react';
import { bool, shape, func } from 'prop-types';

import { extend } from '@thd-nucleus/data-sources';

import { BundleProductsDataModel } from '../../../models/dataModel';
import { BundleMiniPlaceholder } from '../../BundleMiniPlaceholder';
import { LaundryBundleMiniContent } from '../../LaundryBundleMiniContent';
import { useLaundryEntryPoint } from '../../../hooks/useLaundryEntryPoint';

/**
 * BundleLaundryMiniItem is a component that renders a mini item
 * for a laundry entry point. It handles loading states, error handling, and rendering
 * the appropriate content based on the provided data.
 *
 * @param {Object} props - The props object.
 * @param {boolean} props.bundleLoading - Indicates whether the bundle data is loading.
 * @param {Object} props.bundleData - The data for the laundry bundle.
 * @param {Function} props.onError - Callback function to handle errors when rendering fails.
 *
 * @returns {JSX.Element|null} The rendered mini item content, a placeholder during loading,
 * or null if rendering conditions are not met.
 */
export const BundleLaundryMiniItem = ({ bundleLoading, bundleData, onError }) => {
  const {
    productsData, loading: productsLoading, error, swapVariant
  } = useLaundryEntryPoint({ bundle: bundleData });

  const loading = productsLoading || bundleLoading;
  const renderMiniItem = bundleData && productsData && !error;

  useEffect(() => {
    if (!loading && !renderMiniItem) {
      onError();
    }
  }, [renderMiniItem, loading]);

  if (loading) {
    return <BundleMiniPlaceholder />;
  }

  if (!renderMiniItem) {
    return null;
  }

  return (
    <LaundryBundleMiniContent
      products={productsData}
      onSwapVariant={swapVariant}
    />
  );
};

BundleLaundryMiniItem.propTypes = {
  bundleLoading: bool,
  bundleData: shape({}),
  onError: func
};

BundleLaundryMiniItem.defaultProps = {
  bundleLoading: false,
  bundleData: {},
  onError: () => { }
};

BundleLaundryMiniItem.dataModel = extend(
  BundleProductsDataModel,
  LaundryBundleMiniContent
);